import { apiClient } from '@/api/client'

/** 用語集の項目 */
export type BookAiTerm = {
  name: string
  kind: 'person' | 'term' | 'place' | 'organization'
  description: string
  /** 本文中での別の呼び方（略称・姓だけ・肩書きなど） */
  aliases: string[]
}

/** 人物どうし（または人物と組織など）の関係。source / target は人物・用語の名前 */
export type BookAiRelation = {
  source: string
  target: string
  relation: string
}

/** 人物・用語ごとの登場箇所（名前と別名が出てくる段落・ページの位置キー。古い順） */
export type BookAiTermOccurrences = {
  name: string
  kind: BookAiTerm['kind']
  keys: number[]
}

/** 章ごとの AI の結果 */
export type BookAiSectionAnalysis = {
  summary: string
  questions: string[]
  keywords: string[]
  /** 流し読みで強調する文（本文そのまま） */
  key_sentences: string[]
  /** 各重要文の位置キー（その文がある段落の先頭。シークバーの印に使う） */
  key_sentence_keys: number[]
  /** 各重要文の重要度（1: 核心 / 2: 重要 / 3: 補足）。古い解析結果では空 */
  key_sentence_levels: number[]
  terms: BookAiTerm[]
  /** この章で分かる人物の関係 */
  relations: BookAiRelation[]
}

/**
 * 章。start / end は位置キー（EPUB: spine 番号 + 章内の進み具合 / PDF: 0 始まりのページ）。end は含まない
 */
export type BookAiSection = {
  index: number
  title: string
  start: number
  end: number
  char_count: number
  analysis: BookAiSectionAnalysis | null
}

export type BookAiArgument = {
  point: string
  support: string
  section_indexes: number[]
}

/** 本の主張 */
export type BookAiThesis = {
  genre: 'nonfiction' | 'fiction'
  one_liner: string
  main_claim: string
  arguments: BookAiArgument[]
  takeaways: string[]
  open_questions: string[]
}

export type BookAiJob = {
  id: number
  status: 'pending' | 'running' | 'completed' | 'failed'
  progress: number
  total_sections: number
  processed_sections: number
  message: string
  error: string | null
  created_at: string
  completed_at: string | null
}

export type BookAiStatus = {
  episode_id: number
  media: 'epub' | 'pdf' | null
  /** EPUB の readingOrder の href（位置キーの spine 番号に対応） */
  spine: string[]
  sections: BookAiSection[]
  thesis: BookAiThesis | null
  chat_ready: boolean
  job: BookAiJob | null
  /** 古い版の解析結果のままの章の数（解析し直すと新しい機能が使える） */
  outdated_sections: number
  term_index: BookAiTermOccurrences[]
}

/** チャットの回答の根拠にした本文 */
export type BookChatCitation = {
  chunk_id: number
  section_index: number
  section_title: string
  excerpt: string
  key: number
  location: { href?: string; progression?: number; page?: number }
}

export type BookChatMessage = {
  id: number
  role: 'user' | 'assistant'
  content: string
  citations: BookChatCitation[]
  created_at: string
}

type BookChatResponse = {
  question: BookChatMessage
  answer: BookChatMessage
}

// 本文の検索と AI の回答を待つため、通常より長く待つ
const CHAT_TIMEOUT_MS = 2 * 60_000

/** 解析結果とジョブの状態を取得する */
export const getBookAiStatus = async (episodeId: number): Promise<BookAiStatus> => {
  const res = await apiClient.get(`/book-ai/episodes/${episodeId}`)
  return res.data
}

/** 解析ジョブを積む（解析済みの章は飛ばして続きから進む） */
export const requestBookAiAnalysis = async (episodeId: number): Promise<BookAiJob> => {
  const res = await apiClient.post(`/book-ai/episodes/${episodeId}/analyze`)
  return res.data
}

/** 本と対話するチャットの履歴を取得する */
export const getBookChat = async (episodeId: number): Promise<BookChatMessage[]> => {
  const res = await apiClient.get(`/book-ai/episodes/${episodeId}/chat`)
  return res.data
}

/**
 * 本について質問する
 * @param untilKey ネタバレ防止: この位置キーまでの本文だけで答えさせる（null なら本全体）
 */
export const postBookChat = async (
  episodeId: number,
  question: string,
  untilKey: number | null,
): Promise<BookChatResponse> => {
  const res = await apiClient.post(
    `/book-ai/episodes/${episodeId}/chat`,
    { question, until_key: untilKey },
    { timeout: CHAT_TIMEOUT_MS },
  )
  return res.data
}

/** チャットの履歴を消す */
export const clearBookChat = async (episodeId: number): Promise<void> => {
  await apiClient.delete(`/book-ai/episodes/${episodeId}/chat`)
}

/** 「読む前の問い」への AI の答え（読者の答えがあれば講評付き） */
export type QuestionAnswer = {
  answer: string
  feedback: string | null
  citations: BookChatCitation[]
  /** その章に付けた自分のハイライトの数（カードに引用できる） */
  highlight_count: number
}

/** 章の本文だけを根拠に問いに答え、自分の答えがあれば講評してもらう */
export const answerQuestion = async (
  episodeId: number,
  body: { section_index: number; question: string; user_answer: string },
): Promise<QuestionAnswer> => {
  const res = await apiClient.post(`/book-ai/episodes/${episodeId}/questions/answer`, body, {
    timeout: CHAT_TIMEOUT_MS,
  })
  return res.data
}

/** 問いと答えからカード（メモ）を作る */
export const createQuestionCard = async (
  episodeId: number,
  body: {
    section_index: number
    question: string
    user_answer: string
    ai_answer: string | null
    citation_chunk_ids: number[]
    include_highlights: boolean
    /** 似ているカードとつなぐ（カードの末尾にリンクを入れる） */
    link_memo_ids: number[]
  },
): Promise<{ memo_id: number; title: string }> => {
  const res = await apiClient.post(`/book-ai/episodes/${episodeId}/questions/card`, body)
  return res.data
}
